import { selectState as selectStateAuth } from "authentication/main.slice";
import Loader from "components/Loader/Loader";
import Popover from "components/Popover/Popover";
import ToolboxButton, { ToolboxButtonIcon } from "Dashboard/components/Buttons/ToolboxButton";
import { IUpdateInspection } from "Dashboard/scenes/Resources/domain/IUpdateInspection";
import { RESOURCES_MODULE_NAME } from "Dashboard/scenes/Resources/domain/ResourcesModule";
import { unitNameLabel } from "Dashboard/scenes/Resources/helpers/resourceUtil";
import { useAppDispatch } from "hooks/hooks";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo } from "react";
import DatePicker from "react-datepicker";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { hasFullAccessTo, hasWriteAccessTo } from "utils/authUtil";
import { FormatDateShort } from "utils/dateUtils";
import { FormatNumber, FormatNumberWithUnitOrNull, getAdjustedFormatForUnit } from "utils/numberUtils";
import { inspectionTypeDictionary } from "utils/translationDictionary";
import {
  getInspections,
  selectState,
  setRegistrationTachographPopoverState,
  updateInspection,
  updateInspectionTemporarily,
} from "../registrationAndTachograph.slice";
import { selectResourceDetails } from "../../Details/details.slice";
import RegistrationTachographPopover from "./RegistrationTachographPopover";
import InfoTooltip from "components/Popup/InfoTooltip";

interface IProps {
  resourceId: number;
}

const ResourceRegistrationAndTachograph = ({ resourceId }: IProps) => {
  const dispatch = useAppDispatch();

  const { loading, inspections, registrationTachographPopover } =
    useSelector(selectState);
  const resourceDetails = useSelector(selectResourceDetails);
  const isResourceActive = resourceDetails?.resource.status === "ACTIVE"
  const { user } = useSelector(selectStateAuth);
  const totalPrice = (inspections ?? []).reduce(
    (total, inspection) =>
      total + (inspection.price || 0),
    0
  );

  const GetInspections = useCallback(() => {
    dispatch(getInspections(resourceId));
  }, [dispatch, resourceId]);

  useEffect(() => {
    if (resourceId) GetInspections();
  }, [GetInspections, resourceId]);

  const onChangeBasicField = (data: IUpdateInspection) => {
    dispatch(updateInspectionTemporarily(data));
    updateBasicFieldWithDelay(data);
  };

  const updateBasicFieldWithDelay = useMemo(
    () => debounce(async (data: IUpdateInspection) => {
      try {
        await dispatch(updateInspection(data)).unwrap();
        toast.success("Izmena je uspešno zabeležena.");
      } catch (status: any) {
        if (status === 403) {
          toast.error("Neočekivana greška");
        } else if (status === 400) {
          toast.error("Neočekivana greška");
        }
      }
    }, 1000),
    [dispatch]
  );

  return (
    <div className="col-5 loading-container">
      {(!resourceId || loading) && <Loader />}
      <div className="resources-box-header">
        <ToolboxButton
          icon={ToolboxButtonIcon.Add}
          onClick={hasWriteAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive
            ? (e) => dispatch(
              setRegistrationTachographPopoverState({
                isOpen: true,
                top: e.clientY,
                left: e.clientX,
              })
            )
            : undefined
          }
        />
        <span className="title lastTitle">Registracije * Tahografi</span>
        <span></span>
      </div>
      <div className="resources-box-sub-header">
        <span>KM_H / cena</span>
      </div>
      <div className="resources-box-list">
        <div className="resource-wrapp">
          {inspections &&
            inspections.map((inspection, index) => (
              <div className="fourthResource" key={index}>
                <span className="inspection-type">{inspectionTypeDictionary[inspection.type]}</span>
                <span className="date">
                  {hasFullAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive
                    ? (<DatePicker
                      onChange={(date) =>
                        onChangeBasicField({ resourceId: inspection.resource.resourceId, inspectionId: inspection.inspectionId, date: date ? date.toISOString().split("T")[0] : undefined })
                      }
                      showMonthDropdown
                      showYearDropdown
                      dateFormat="dd.MM.yy"
                      className="date"
                      selected={inspection.date ? new Date(inspection.date) : null}
                    />)
                    : FormatDateShort(inspection.date)
                  }
                </span>
                <InfoTooltip tooltip={FormatNumberWithUnitOrNull(inspection.price, 'rsd') ?? undefined}
                  classesProp="price bg-text-overflow" element="span">
                  {FormatNumberWithUnitOrNull(inspection.price, 'rsd')}
                </InfoTooltip>
                <span className="price-label">cena</span>
                <span className="usage-unit">{unitNameLabel(inspection.usageUnit)}</span>
                <span className="usage-amount">{FormatNumber(inspection.usageAmount, getAdjustedFormatForUnit(inspection.usageUnit?.name))}</span>
                <span className="note">
                  <input
                    type="text"
                    value={inspection.note || ""}
                    onChange={(e) => {
                      onChangeBasicField({ resourceId: inspection.resource.resourceId, inspectionId: inspection.inspectionId, note: e.target.value })
                    }}
                    className="note"
                    disabled={!(hasWriteAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive)}
                  />
                </span>
              </div>
            ))}
          {inspections.length < 4 && <div className="fourthResource"></div>}
          {inspections.length < 3 && <div className="fourthResource"></div>}
          {inspections.length < 2 && <div className="fourthResource"></div>}
          {inspections.length < 1 && <div className="fourthResource"></div>}
        </div>
      </div>
      <div className="secondFooter">
        <span className="w60">{FormatNumberWithUnitOrNull(totalPrice, 'rsd')}</span>
      </div>
      <Popover
        isOpen={registrationTachographPopover.isOpen}
        onClose={() => {
          dispatch(setRegistrationTachographPopoverState({ isOpen: false }));
        }}
        title="Registracija"
        direction="left"
        arrowStyle={{ top: "50%" }}
        position={{
          top: -607,
          left: 1048,
          offsetByWindowSizeY: true
        }}
      >
        <RegistrationTachographPopover onModalChange={GetInspections} />
      </Popover>
    </div>
  );
};

export default ResourceRegistrationAndTachograph;
